// Offline operations queue backed by IndexedDB. Supports:
//   op="create_customer" -> {temp_id, payload}
//   op="create_rental"   -> {payload, receipt}  (payload.customer_id may be a temp_id)
//   op="edit_rental"      -> {rental_id, payload}
// Records auto-sync (in dependency order) when connectivity returns.

const DB_NAME = "rentalpro-offline";
const DB_VERSION = 1;
const STORE = "rental_queue";
export const QUEUE_EVENT = "rp-queue-changed";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "local_id" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function reqPromise(request) {
  return new Promise((res, rej) => {
    request.onsuccess = () => res(request.result);
    request.onerror = () => rej(request.error);
  });
}

function notifyChange() {
  try { window.dispatchEvent(new Event(QUEUE_EVENT)); } catch (e) { /* noop */ }
}

export async function addToQueue(record) {
  const db = await openDB();
  await reqPromise(db.transaction(STORE, "readwrite").objectStore(STORE).put(record));
  notifyChange();
  return record;
}

export async function getQueue() {
  const db = await openDB();
  const all = await reqPromise(db.transaction(STORE, "readonly").objectStore(STORE).getAll());
  return (all || []).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
}

export async function getQueued(localId) {
  const db = await openDB();
  return reqPromise(db.transaction(STORE, "readonly").objectStore(STORE).get(localId));
}

export async function updateQueued(localId, patch) {
  const rec = await getQueued(localId);
  if (!rec) return null;
  const merged = { ...rec, ...patch };
  await addToQueue(merged);
  return merged;
}

export async function removeFromQueue(localId) {
  const db = await openDB();
  await reqPromise(db.transaction(STORE, "readwrite").objectStore(STORE).delete(localId));
  notifyChange();
}

// Reset all errored records back to pending so the next flush retries them.
export async function retryAll() {
  const items = await getQueue();
  for (const rec of items) {
    if (rec.status === "error") await addToQueue({ ...rec, status: "pending", error: null });
  }
}

const isNetworkError = (err) => !err?.response; // no HTTP response => offline / server unreachable

// Extract a human-readable string from an API error (FastAPI 422 detail can be
// an array/object). Prevents storing an object into rec.error (React child crash).
function errMsg(err) {
  const d = err?.response?.data?.detail;
  if (!d) return "Gagal sinkron";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || x?.detail || "Data tidak valid").join("; ");
  return d?.msg || d?.detail || "Gagal sinkron";
}

let flushing = false;

// handlers: { create_customer(payload), create_rental(payload), edit_rental(rentalId, payload) }
export async function flushQueue(handlers) {
  if (flushing || !navigator.onLine) return { synced: 0, failed: 0, skipped: true };
  flushing = true;
  let synced = 0, failed = 0;
  const idMap = {}; // temp customer id -> real id

  const markError = async (rec, err) => {
    await addToQueue({ ...rec, status: "error", error: errMsg(err) });
    failed += 1;
  };

  try {
    const items = (await getQueue()).filter((r) => r.status !== "error");

    // 1) Customers first so temp ids resolve.
    for (const rec of items.filter((r) => r.op === "create_customer")) {
      try {
        const data = await handlers.create_customer(rec.payload);
        idMap[rec.temp_id] = data.id;
        await removeFromQueue(rec.local_id);
        synced += 1;
      } catch (err) {
        if (isNetworkError(err)) return { synced, failed };
        await markError(rec, err);
      }
    }

    // 2) Rental creations (remap temp customer id).
    for (const rec of items.filter((r) => !r.op || r.op === "create_rental")) {
      try {
        const payload = { ...rec.payload };
        if (payload.customer_id && idMap[payload.customer_id]) payload.customer_id = idMap[payload.customer_id];
        if (String(payload.customer_id || "").startsWith("localcust-")) {
          // its customer hasn't synced yet (probably failed) - retry later
          failed += 1;
          continue;
        }
        await handlers.create_rental(payload);
        await removeFromQueue(rec.local_id);
        synced += 1;
      } catch (err) {
        if (isNetworkError(err)) return { synced, failed };
        await markError(rec, err);
      }
    }

    // 3) Edits to existing server rentals.
    for (const rec of items.filter((r) => r.op === "edit_rental")) {
      try {
        await handlers.edit_rental(rec.rental_id, rec.payload);
        await removeFromQueue(rec.local_id);
        synced += 1;
      } catch (err) {
        if (isNetworkError(err)) return { synced, failed };
        await markError(rec, err);
      }
    }
  } finally {
    flushing = false;
  }
  return { synced, failed };
}
